import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import BackMethods from "../../back_component/BackMethods";
import {
    FaFileExcel, FaSearch, FaTable, FaChartLine, FaClock,
    FaMapMarkerAlt, FaBuilding, FaArrowUp, FaArrowDown
} from "react-icons/fa";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const PriceAggregationReportPage = () => {
    const [searchParams] = useSearchParams();
    const { backEnd_request } = BackMethods();

    const [reports, setReports] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const groupByOptions = [
        { value: "establishment", label: "حسب المنشأة" },
        { value: "city", label: "حسب المدينة" },
        { value: "governorate", label: "حسب المحافظة" }
    ];

    const currencyOptions = [
        { value: "SYP", label: "ليرة سورية (SYP)" },
        { value: "USD", label: "دولار أمريكي ($)" }
    ];

    const [filters, setFilters] = useState({
        from: "",
        to: "",
        productId: searchParams.get("productId") || "",
        establishmentUid: searchParams.get("establishmentUid") || "",
        city: "",
        governorate: "",
        groupBy: "establishment",
        currency: "SYP"
    });

    const isSearchDisabled = !filters.from || !filters.to || !filters.productId;

    // --- التعديل الأول: توحيد مفتاح التاريخ لمنع التكرار ---
    const { chartData, uniqueLevels } = useMemo(() => {
        if (!reports || reports.length === 0) return { chartData: [], uniqueLevels: [] };

        const levels = [...new Set(reports.map(item => item.levelKey))];

        const dataByDate = reports.reduce((acc, item) => {
            // نستخدم التاريخ فقط بدون الوقت كمفتاح (Key) لضمان دمج المدن في نفس اليوم
            const dateOnly = new Date(item.dayOrCapturedAtUtc).toLocaleDateString('en-CA'); // YYYY-MM-DD
            const displayFormat = new Date(item.dayOrCapturedAtUtc).toLocaleDateString('ar-SY', { day: '2-digit', month: 'short' });

            if (!acc[dateOnly]) {
                acc[dateOnly] = {
                    dateKey: dateOnly,
                    displayDate: displayFormat,
                };
            }
            // ربط السعر بالجهة (دمشق، حلب، إلخ) داخل نفس اليوم
            acc[dateOnly][item.levelKey] = item.avg;
            return acc;
        }, {});

        // ترتيب البيانات زمنياً بناءً على التاريخ
        const sortedData = Object.values(dataByDate).sort((a, b) => new Date(a.dateKey) - new Date(b.dateKey));

        return { chartData: sortedData, uniqueLevels: levels };
    }, [reports]);

    const colors = ["#0d6efd", "#198754", "#dc3545", "#ffc107", "#6610f2", "#fd7e14", "#0dcaf0"];

    const loadEstablishments = async (inputValue) => {
        if (!inputValue) return [];
        try {
            const res = await backEnd_request.get(`/Establishments?q=${inputValue}&page=1&size=20`);
            return res.data.data.map(est => ({
                value: est.fieldUID,
                label: `${est.name} (${est.district || est.city})`
            }));
        } catch (err) { return []; }
    };

    const fetchProducts = useCallback(async () => {
        try {
            const res = await backEnd_request.get("/PriceSurveyProducts?includeInactive=true");
            setProducts(res.data.map(p => ({ value: p.priceSurveyProductId, label: p.nameAr || p.name })));
        } catch (err) { console.error(err); }
    }, [backEnd_request]);

    const fetchReportData = async () => {
        if (isSearchDisabled) return;
        setLoading(true);
        try {
            const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
            const queryParams = new URLSearchParams(cleanFilters).toString();
            const res = await backEnd_request.get(`/PriceSurveys/product-between?${queryParams}`);
            setReports(res.data || []);
        } catch (err) {
            console.error(err);
            setReports([]);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const customStyles = {
        control: (base) => ({ ...base, borderRadius: '8px', minHeight: '40px', fontSize: '14px' })
    };

    return (
        <div className="container-fluid px-4 py-4" dir="rtl" style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
            {/* Header & Filters ... (نفس الكود السابق) */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-dark m-0"><FaTable className="ms-2 text-primary" /> تقرير مجمع لأسعار المواد</h3>
                <button className="btn btn-success shadow-sm d-flex align-items-center fw-bold" disabled={reports.length === 0}>
                    <FaFileExcel className="ms-2" /> تصدير Excel
                </button>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="small fw-bold mb-2 text-danger">المادة *</label>
                            <Select options={products} placeholder="اختر مادة..." styles={customStyles} onChange={(opt) => setFilters({ ...filters, productId: opt?.value || "" })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2 text-danger">من تاريخ *</label>
                            <input type="date" className="form-control" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2 text-danger">إلى تاريخ *</label>
                            <input type="date" className="form-control" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">تجميع حسب</label>
                            <Select options={groupByOptions} defaultValue={groupByOptions[0]} styles={customStyles} onChange={(opt) => setFilters({ ...filters, groupBy: opt.value })} />
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold mb-2">المنشأة</label>
                            <AsyncSelect cacheOptions loadOptions={loadEstablishments} placeholder="بحث عن منشأة..." styles={customStyles} isClearable onChange={(opt) => setFilters({ ...filters, establishmentUid: opt?.value || "" })} />
                        </div>

                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">المحافظة</label>
                            <input type="text" className="form-control" placeholder="دمشق" onChange={(e) => setFilters({ ...filters, governorate: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">المدينة</label>
                            <input type="text" className="form-control" placeholder="المزة" onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">العملة</label>
                            <Select options={currencyOptions} defaultValue={currencyOptions[0]} styles={customStyles} onChange={(opt) => setFilters({ ...filters, currency: opt.value })} />
                        </div>

                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary w-100 fw-bold shadow-sm" style={{ height: '40px' }} onClick={fetchReportData} disabled={isSearchDisabled || loading}>
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><FaSearch className="ms-2" /> بحث وتحليل</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {reports.length > 0 && (
                <>
                    <div className="card border-0 shadow-sm rounded-4 mb-4 p-4">
                        <h5 className="fw-bold mb-4 text-dark d-flex align-items-center">
                            <FaChartLine className="ms-2 text-info" />
                            مقارنة تغير السعر عبر الزمن ({filters.groupBy === 'city' ? 'للمدن' : filters.groupBy === 'governorate' ? 'للمحافظات' : 'للفعاليات'})
                        </h5>
                        <div style={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />

                                    {/* التعديل الثاني: استخدام displayDate كنص فئوي لمنع التكرار */}
                                    <XAxis
                                        dataKey="displayDate"
                                        tick={{ fontSize: 12 }}
                                        padding={{ left: 30, right: 30 }}
                                    />

                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        domain={[0, 'auto']}
                                        tickFormatter={(v) => v.toLocaleString()}
                                    />

                                    <Tooltip
                                        contentStyle={{ textAlign: 'right', borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Legend verticalAlign="top" height={40} iconType="circle" />

                                    {uniqueLevels.map((level, index) => (
                                        <Line
                                            key={level}
                                            type="monotone"
                                            dataKey={level}
                                            name={level}
                                            stroke={colors[index % colors.length]}
                                            strokeWidth={3}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 7 }}
                                            connectNulls
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* الجدول (نفس الكود السابق) */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 text-end">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="py-3 ps-4">التاريخ والوقت</th>
                                        <th>الجهة / النطاق</th>
                                        <th className="text-center">العدد</th>
                                        <th className="text-center">المتوسط</th>
                                        <th className="text-center">أدنى</th>
                                        <th className="text-center">أعلى</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((item, index) => (
                                        <tr key={index}>
                                            <td className="ps-4">
                                                <div className="fw-bold">{new Date(item.dayOrCapturedAtUtc).toLocaleDateString('ar-SY')}</div>
                                                <small className="text-muted"><FaClock className="ms-1" /> {new Date(item.dayOrCapturedAtUtc).toLocaleTimeString('ar-SY', { hour: '2-digit', minute: '2-digit' })}</small>
                                            </td>
                                            <td>
                                                <div className="fw-bold text-dark">
                                                    {filters.groupBy === "establishment" ? <FaBuilding className="ms-2 text-primary opacity-50" /> : <FaMapMarkerAlt className="ms-2 text-primary opacity-50" />}
                                                    {item.levelKey}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-secondary-subtle text-secondary px-3">{item.count}</span>
                                            </td>
                                            <td className="text-center fw-bold text-primary">
                                                {item.avg.toLocaleString()} <small className="fw-normal">{item.currency}</small>
                                            </td>
                                            <td className="text-center text-success">
                                                <FaArrowDown className="me-1 small" />{item.min.toLocaleString()}
                                            </td>
                                            <td className="text-center text-danger">
                                                <FaArrowUp className="me-1 small" />{item.max.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {!loading && reports.length === 0 && (
                <div className="text-center py-5 bg-white rounded-4 shadow-sm border mt-4">
                    <div className="text-muted mb-2 fs-5">لا توجد نتائج مطابقة</div>
                    <div className="small text-muted">يرجى اختيار المادة والنطاق الزمني والضغط على بحث</div>
                </div>
            )}
        </div>
    );
};

export default PriceAggregationReportPage;